// Tcube Full Body Health Analysis: Computation Engine
// Pure functions. Every calculation returns null (or "Insufficient Data") when required inputs are missing.

const num = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
};

// ---------- CORE FORMULAS ----------

export const calcBMI = (weightKg, heightCm) => {
  const w = num(weightKg), h = num(heightCm);
  if (!w || !h) return null;
  const m = h / 100;
  return +(w / (m * m)).toFixed(1);
};

export const bmiCategory = (bmi) => {
  if (bmi == null) return null;
  if (bmi < 18.5) return { label: 'Underweight', tone: 'amber', risk: 'moderate' };
  if (bmi < 25) return { label: 'Normal', tone: 'emerald', risk: 'low' };
  if (bmi < 30) return { label: 'Overweight', tone: 'orange', risk: 'moderate' };
  return { label: 'Obese', tone: 'rose', risk: 'high' };
};

export const calcLBM = (weightKg, bodyFatPct) => {
  const w = num(weightKg), bf = num(bodyFatPct);
  if (!w || bf == null || bf <= 0 || bf >= 80) return null;
  return +(w * (1 - bf / 100)).toFixed(1);
};

export const calcWHtR = (waistCm, heightCm) => {
  const w = num(waistCm), h = num(heightCm);
  if (!w || !h) return null;
  return +(w / h).toFixed(2);
};

export const whtrCategory = (whtr) => {
  if (whtr == null) return null;
  if (whtr < 0.5) return { label: 'Healthy', tone: 'emerald', risk: 'low' };
  if (whtr < 0.6) return { label: 'Increased Risk', tone: 'amber', risk: 'moderate' };
  return { label: 'High Risk', tone: 'rose', risk: 'high' };
};

export const calcWHR = (waistCm, hipCm) => {
  const w = num(waistCm), h = num(hipCm);
  if (!w || !h) return null;
  return +(w / h).toFixed(2);
};

export const whrCategory = (whr, gender) => {
  if (whr == null) return null;
  if (gender === 'male') {
    if (whr < 0.9) return { label: 'Low Risk', tone: 'emerald', risk: 'low' };
    if (whr < 1.0) return { label: 'Moderate Risk', tone: 'amber', risk: 'moderate' };
    return { label: 'High Risk', tone: 'rose', risk: 'high' };
  }
  if (gender === 'female') {
    if (whr < 0.8) return { label: 'Low Risk', tone: 'emerald', risk: 'low' };
    if (whr < 0.85) return { label: 'Moderate Risk', tone: 'amber', risk: 'moderate' };
    return { label: 'High Risk', tone: 'rose', risk: 'high' };
  }
  return null;
};

export const calcEAG = (hba1c) => {
  const h = num(hba1c);
  if (h == null) return null;
  return Math.round(28.7 * h - 46.7);
};

export const hba1cCategory = (hba1c) => {
  const h = num(hba1c);
  if (h == null) return null;
  if (h < 5.7) return { label: 'Normal', tone: 'emerald', risk: 'low' };
  if (h < 6.5) return { label: 'Prediabetes', tone: 'amber', risk: 'moderate' };
  return { label: 'Diabetes Range', tone: 'rose', risk: 'high' };
};

export const fbsCategory = (fbs) => {
  const v = num(fbs);
  if (v == null) return null;
  if (v < 100) return { label: 'Normal', tone: 'emerald', risk: 'low' };
  if (v < 126) return { label: 'Prediabetes', tone: 'amber', risk: 'moderate' };
  return { label: 'Diabetes Range', tone: 'rose', risk: 'high' };
};

export const ppbsCategory = (ppbs) => {
  const v = num(ppbs);
  if (v == null) return null;
  if (v < 140) return { label: 'Normal', tone: 'emerald', risk: 'low' };
  if (v < 200) return { label: 'Prediabetes', tone: 'amber', risk: 'moderate' };
  return { label: 'Diabetes Range', tone: 'rose', risk: 'high' };
};

export const calcHOMAIR = (fastingInsulin, fbs) => {
  const ins = num(fastingInsulin), g = num(fbs);
  if (!ins || !g) return null;
  return +((ins * g) / 405).toFixed(2);
};

export const homaIRCategory = (homa) => {
  if (homa == null) return null;
  if (homa < 1) return { label: 'Excellent', tone: 'emerald', risk: 'low' };
  if (homa < 2) return { label: 'Normal', tone: 'emerald', risk: 'low' };
  if (homa < 3) return { label: 'Early Insulin Resistance', tone: 'amber', risk: 'moderate' };
  return { label: 'Significant Insulin Resistance', tone: 'rose', risk: 'high' };
};

export const calcMAP = (sbp, dbp) => {
  const s = num(sbp), d = num(dbp);
  if (!s || !d) return null;
  return Math.round((s + 2 * d) / 3);
};

export const mapCategory = (map) => {
  if (map == null) return null;
  if (map < 60) return { label: 'Low Perfusion', tone: 'amber', risk: 'moderate' };
  if (map <= 100) return { label: 'Normal', tone: 'emerald', risk: 'low' };
  return { label: 'Elevated Pressure', tone: 'rose', risk: 'high' };
};

export const bpCategory = (sbp, dbp) => {
  const s = num(sbp), d = num(dbp);
  if (!s || !d) return null;
  if (s < 120 && d < 80) return { label: 'Normal', tone: 'emerald', risk: 'low' };
  if (s < 130 && d < 80) return { label: 'Elevated', tone: 'amber', risk: 'moderate' };
  if (s < 140 || d < 90) return { label: 'Stage 1 Hypertension', tone: 'orange', risk: 'moderate' };
  return { label: 'Stage 2 Hypertension', tone: 'rose', risk: 'high' };
};

export const calcPulsePressure = (sbp, dbp) => {
  const s = num(sbp), d = num(dbp);
  if (!s || !d) return null;
  return Math.round(s - d);
};

export const pulsePressureCategory = (pp) => {
  if (pp == null) return null;
  if (pp >= 40 && pp <= 60) return { label: 'Normal', tone: 'emerald', risk: 'low' };
  if (pp > 60) return { label: 'Arterial Stiffness Risk', tone: 'rose', risk: 'high' };
  return { label: 'Low: Check Hydration', tone: 'amber', risk: 'moderate' };
};

export const calcLDLHDL = (ldl, hdl) => {
  const l = num(ldl), h = num(hdl);
  if (!l || !h) return null;
  return +(l / h).toFixed(2);
};

export const ldlHdlCategory = (ratio) => {
  if (ratio == null) return null;
  if (ratio < 2.5) return { label: 'Excellent', tone: 'emerald', risk: 'low' };
  if (ratio <= 3.5) return { label: 'Average', tone: 'amber', risk: 'moderate' };
  return { label: 'Increased Cardiovascular Risk', tone: 'rose', risk: 'high' };
};

export const calcTGHDL = (tg, hdl) => {
  const t = num(tg), h = num(hdl);
  if (!t || !h) return null;
  return +(t / h).toFixed(2);
};

export const tgHdlCategory = (ratio) => {
  if (ratio == null) return null;
  if (ratio < 2) return { label: 'Excellent', tone: 'emerald', risk: 'low' };
  if (ratio <= 4) return { label: 'Moderate Risk', tone: 'amber', risk: 'moderate' };
  return { label: 'High Insulin Resistance Risk', tone: 'rose', risk: 'high' };
};

export const restingHRCategory = (hr) => {
  const v = num(hr);
  if (v == null) return null;
  if (v < 40) return { label: 'Very Low', tone: 'amber', risk: 'moderate' };
  if (v <= 60) return { label: 'Athlete', tone: 'emerald', risk: 'low' };
  if (v <= 100) return { label: 'Normal', tone: 'emerald', risk: 'low' };
  return { label: 'Tachycardia Risk', tone: 'rose', risk: 'high' };
};

export const bodyFatCategory = (bf, gender) => {
  const v = num(bf);
  if (v == null) return null;
  if (gender === 'male') {
    if (v < 6) return { label: 'Essential Fat', tone: 'amber', risk: 'moderate' };
    if (v < 18) return { label: 'Fitness', tone: 'emerald', risk: 'low' };
    if (v < 25) return { label: 'Average', tone: 'amber', risk: 'moderate' };
    return { label: 'Obese', tone: 'rose', risk: 'high' };
  }
  if (gender === 'female') {
    if (v < 14) return { label: 'Essential Fat', tone: 'amber', risk: 'moderate' };
    if (v < 25) return { label: 'Fitness', tone: 'emerald', risk: 'low' };
    if (v < 32) return { label: 'Average', tone: 'amber', risk: 'moderate' };
    return { label: 'Obese', tone: 'rose', risk: 'high' };
  }
  return null;
};

// ---------- SCORING ----------
// Each sub-score returns 0–100. Higher = healthier. null = insufficient data.

const riskToScore = (risk) => {
  if (risk === 'low') return 90;
  if (risk === 'moderate') return 60;
  if (risk === 'high') return 25;
  return null;
};

const avgDefined = (arr) => {
  const defined = arr.filter((x) => x != null);
  if (!defined.length) return null;
  return Math.round(defined.reduce((a, b) => a + b, 0) / defined.length);
};

export const computeAll = (input) => {
  const i = input || {};
  const c = {};

  // Basic
  c.bmi = calcBMI(i.weight_kg, i.height_cm);
  c.bmi_cat = bmiCategory(c.bmi);
  c.lbm = calcLBM(i.weight_kg, i.body_fat_pct);
  c.whtr = calcWHtR(i.waist_cm, i.height_cm);
  c.whtr_cat = whtrCategory(c.whtr);
  c.whr = calcWHR(i.waist_cm, i.hip_cm);
  c.whr_cat = whrCategory(c.whr, i.gender);

  // Diabetes
  c.eag = calcEAG(i.hba1c);
  c.hba1c_cat = hba1cCategory(i.hba1c);
  c.fbs_cat = fbsCategory(i.fbs);
  c.ppbs_cat = ppbsCategory(i.ppbs);
  c.homa_ir = calcHOMAIR(i.fasting_insulin, i.fbs);
  c.homa_cat = homaIRCategory(c.homa_ir);

  // Cardiovascular
  c.map = calcMAP(i.sbp, i.dbp);
  c.map_cat = mapCategory(c.map);
  c.bp_cat = bpCategory(i.sbp, i.dbp);
  c.pulse_pressure = calcPulsePressure(i.sbp, i.dbp);
  c.pp_cat = pulsePressureCategory(c.pulse_pressure);

  // Lipid
  c.ldl_hdl = calcLDLHDL(i.ldl, i.hdl);
  c.ldl_hdl_cat = ldlHdlCategory(c.ldl_hdl);
  c.tg_hdl = calcTGHDL(i.triglycerides, i.hdl);
  c.tg_hdl_cat = tgHdlCategory(c.tg_hdl);

  // Fitness
  c.resting_hr_cat = restingHRCategory(i.resting_hr);
  c.body_fat_cat = bodyFatCategory(i.body_fat_pct, i.gender);

  return c;
};

export const computeScores = (computed, input) => {
  const c = computed || {};
  const i = input || {};

  const metabolic = avgDefined([
    c.hba1c_cat && riskToScore(c.hba1c_cat.risk),
    c.fbs_cat && riskToScore(c.fbs_cat.risk),
    c.ppbs_cat && riskToScore(c.ppbs_cat.risk),
    c.homa_cat && riskToScore(c.homa_cat.risk),
    c.tg_hdl_cat && riskToScore(c.tg_hdl_cat.risk),
    c.whtr_cat && riskToScore(c.whtr_cat.risk),
    c.whr_cat && riskToScore(c.whr_cat.risk)
  ]);

  const cardiovascular = avgDefined([
    c.bp_cat && riskToScore(c.bp_cat.risk),
    c.map_cat && riskToScore(c.map_cat.risk),
    c.pp_cat && riskToScore(c.pp_cat.risk),
    c.ldl_hdl_cat && riskToScore(c.ldl_hdl_cat.risk),
    c.resting_hr_cat && riskToScore(c.resting_hr_cat.risk)
  ]);

  const diabetes = avgDefined([
    c.hba1c_cat && riskToScore(c.hba1c_cat.risk),
    c.fbs_cat && riskToScore(c.fbs_cat.risk),
    c.ppbs_cat && riskToScore(c.ppbs_cat.risk),
    c.homa_cat && riskToScore(c.homa_cat.risk)
  ]);

  const obesity = avgDefined([
    c.bmi_cat && riskToScore(c.bmi_cat.risk),
    c.body_fat_cat && riskToScore(c.body_fat_cat.risk),
    c.whtr_cat && riskToScore(c.whtr_cat.risk),
    c.whr_cat && riskToScore(c.whr_cat.risk)
  ]);

  const activityScore = ({ sedentary: 40, light: 60, moderate: 75, active: 85, athlete: 95 })[i.activity_level] ?? null;
  const fitness = avgDefined([
    c.resting_hr_cat && riskToScore(c.resting_hr_cat.risk),
    c.body_fat_cat && riskToScore(c.body_fat_cat.risk),
    activityScore,
    c.bmi_cat && riskToScore(c.bmi_cat.risk)
  ]);

  const overall = avgDefined([metabolic, cardiovascular, diabetes, obesity, fitness]);

  return { overall, metabolic, cardiovascular, diabetes, obesity, fitness };
};

// Health Data Completeness: % of meaningful fields filled
const KEY_FIELDS = [
  'age', 'gender', 'height_cm', 'weight_kg', 'waist_cm', 'hip_cm', 'neck_cm', 'activity_level',
  'sbp', 'dbp', 'hba1c', 'fbs', 'ppbs', 'fasting_insulin',
  'total_cholesterol', 'ldl', 'hdl', 'triglycerides',
  'resting_hr', 'body_fat_pct'
];

export const computeCompleteness = (input) => {
  const i = input || {};
  let filled = 0;
  for (const k of KEY_FIELDS) {
    const v = i[k];
    if (v !== null && v !== undefined && v !== '') filled += 1;
  }
  return { filled, total: KEY_FIELDS.length, percent: Math.round((filled / KEY_FIELDS.length) * 100) };
};

// Score color mapping
export const scoreColor = (score) => {
  if (score == null) return { text: 'text-slate-400', from: 'from-slate-700', to: 'to-slate-600', label: 'Insufficient Data' };
  if (score >= 80) return { text: 'text-emerald-400', from: 'from-emerald-500', to: 'to-green-500', label: 'Healthy' };
  if (score >= 65) return { text: 'text-amber-400', from: 'from-amber-500', to: 'to-yellow-500', label: 'Moderate Risk' };
  if (score >= 45) return { text: 'text-orange-400', from: 'from-orange-500', to: 'to-amber-500', label: 'High Risk' };
  return { text: 'text-rose-400', from: 'from-rose-500', to: 'to-red-500', label: 'Critical Attention' };
};

export const toneClasses = (tone) => {
  switch (tone) {
    case 'emerald': return { text: 'text-emerald-400', border: 'border-emerald-500/30', bg: 'bg-emerald-500/10' };
    case 'amber': return { text: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500/10' };
    case 'orange': return { text: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/10' };
    case 'rose': return { text: 'text-rose-400', border: 'border-rose-500/30', bg: 'bg-rose-500/10' };
    default: return { text: 'text-slate-400', border: 'border-slate-700', bg: 'bg-slate-800/40' };
  }
};

// ---------- AI-STYLE INSIGHTS ----------

export const buildSummary = (computed, scores, input) => {
  const lines = [];
  const c = computed || {};
  const i = input || {};

  if (c.bmi_cat?.risk === 'high' || c.body_fat_cat?.risk === 'high') {
    lines.push('Your body composition markers indicate elevated adiposity, which may contribute to metabolic and cardiovascular load.');
  }
  if (c.homa_cat?.risk === 'high' || c.homa_cat?.risk === 'moderate') {
    lines.push('Your insulin resistance markers suggest early-to-moderate insulin sensitivity decline; focused nutrition and resistance training may help.');
  }
  if (c.hba1c_cat?.risk === 'moderate' || c.hba1c_cat?.risk === 'high') {
    lines.push('Glycemic markers indicate elevated long-term glucose exposure, so consider closer monitoring and a low-glycemic dietary pattern.');
  }
  if (c.bp_cat?.risk === 'high' || c.bp_cat?.risk === 'moderate') {
    lines.push('Blood pressure values are above the optimal range. Sodium moderation, regular aerobic activity, and stress management are usually beneficial.');
  }
  if (c.ldl_hdl_cat?.risk === 'high' || c.tg_hdl_cat?.risk === 'high') {
    lines.push('Lipid ratios indicate increased cardiovascular load; emphasise omega-3 intake, soluble fibre and consistent aerobic exercise.');
  }
  if (c.whtr_cat?.risk === 'high' || c.whr_cat?.risk === 'high') {
    lines.push('Abdominal fat distribution is elevated. Central adiposity is a strong independent predictor of metabolic risk.');
  }
  if (!lines.length && (scores?.overall || 0) >= 80) {
    lines.push('Your provided markers are largely within healthy ranges. Maintain consistent exercise, sleep and nutrition to preserve these results.');
  }
  if (!lines.length) {
    lines.push('Add more health markers to unlock detailed insights and risk interpretation.');
  }
  // Activity nudge
  if (i.activity_level === 'sedentary') {
    lines.push('Reported activity level is sedentary. Increasing daily movement and adding 2–3 resistance sessions weekly typically delivers fast metabolic improvements.');
  }
  return lines;
};

export const buildRecommendations = (computed, scores, input) => {
  const recs = { nutrition: [], exercise: [], lifestyle: [], sleep: [], hydration: [] };
  const c = computed || {};
  const i = input || {};

  // Nutrition
  if (c.hba1c_cat?.risk !== 'low' || c.homa_cat?.risk === 'moderate' || c.homa_cat?.risk === 'high') {
    recs.nutrition.push('Prioritise low-glycemic carbohydrates: vegetables, legumes, intact whole grains.');
    recs.nutrition.push('Include 25–35 g of fibre daily; balance every meal with adequate protein.');
  }
  if (c.ldl_hdl_cat?.risk !== 'low' || c.tg_hdl_cat?.risk !== 'low') {
    recs.nutrition.push('Increase omega-3 sources (oily fish, walnuts, flaxseed) and reduce ultra-processed foods.');
  }
  if (c.bmi_cat?.risk !== 'low' || c.whtr_cat?.risk !== 'low') {
    recs.nutrition.push('Create a modest 300–500 kcal daily deficit; aim for ~0.5 kg per week of sustainable fat loss.');
  }
  if (!recs.nutrition.length) {
    recs.nutrition.push('Maintain a balanced, whole-food pattern with adequate protein at every meal.');
  }

  // Exercise
  if (i.activity_level === 'sedentary' || i.activity_level === 'light') {
    recs.exercise.push('Build up to 150 minutes of moderate aerobic activity per week.');
  }
  if (c.body_fat_cat?.risk === 'moderate' || c.body_fat_cat?.risk === 'high' || c.bmi_cat?.risk !== 'low') {
    recs.exercise.push('Add 2–3 full-body resistance training sessions weekly to preserve and build lean mass.');
  }
  recs.exercise.push('Include 1–2 short interval sessions weekly for cardiovascular efficiency.');

  // Lifestyle
  if (c.bp_cat?.risk !== 'low' || (scores?.cardiovascular ?? 100) < 80) {
    recs.lifestyle.push('Reduce sodium intake and limit alcohol; manage stress with breath work or short walks.');
  }
  recs.lifestyle.push('Avoid smoking and recreational substances that affect metabolic health.');

  // Sleep
  recs.sleep.push('Aim for 7–9 hours of consistent sleep with a regular wake time.');
  recs.sleep.push('Avoid screens 60 minutes before bed and keep the room cool and dark.');

  // Hydration
  if (i.weight_kg) {
    const litres = +(i.weight_kg * 0.035).toFixed(1);
    recs.hydration.push(`Target around ${litres} L of water daily, more on training days.`);
  } else {
    recs.hydration.push('Aim for clear-to-pale-straw urine as a hydration cue.');
  }

  return recs;
};
